import React, { useState } from 'react';
import { Plus, Car, X, Save, Loader2, FileText, Upload, CheckCircle, Clock, XCircle, Trash2, Edit2 } from 'lucide-react';
import { api } from '../../services/api';
import { compressIfImage } from '../../utils/imageCompress';

const DOC_LABELS = {
  vehicle_registration: 'Fahrzeugzulassung',
  vehicle_photo: 'Fahrzeugfoto',
  insurance: 'Versicherungsnachweis',
};

const STATUS_BADGE = {
  approved: { label: 'Freigegeben', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  pending: { label: 'Warten auf Freigabe', cls: 'bg-yellow-100 text-yellow-700', icon: Clock },
  rejected: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-700', icon: XCircle },
};

const VEHICLE_TYPES = [
  { value: 'economy', label: 'Economy' }, { value: 'comfort', label: 'Comfort' },
  { value: 'business', label: 'Business' }, { value: 'premium', label: 'Premium' },
  { value: 'suv', label: 'SUV' }, { value: 'van', label: 'Van' },
  { value: 'minibus', label: 'Kleinbus' }, { value: 'bus', label: 'Bus' },
];

const emptyVehicle = { make: '', model: '', year: 2024, color: '', plate_number: '', vehicle_type: 'comfort', passengers: 4, luggage: 3 };

function VehicleForm({ form, setForm, onSave, saveLabel, saving, onCancel }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Hersteller</label>
        <input value={form.make} onChange={e => setForm(f => ({ ...f, make: e.target.value }))}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="vehicle-make-input" />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Modell</label>
        <input value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="vehicle-model-input" />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Baujahr</label>
        <input type="number" value={form.year} onChange={e => setForm(f => ({ ...f, year: Number(e.target.value) }))}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Farbe</label>
        <input value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Kennzeichen</label>
        <input value={form.plate_number} onChange={e => setForm(f => ({ ...f, plate_number: e.target.value }))}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Fahrzeugtyp</label>
        <select value={form.vehicle_type} onChange={e => setForm(f => ({ ...f, vehicle_type: e.target.value }))}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
          {VEHICLE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Passagiere</label>
        <input type="number" value={form.passengers} onChange={e => setForm(f => ({ ...f, passengers: Number(e.target.value) }))}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Gepäck</label>
        <input type="number" value={form.luggage} onChange={e => setForm(f => ({ ...f, luggage: Number(e.target.value) }))}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
      </div>
      <div className="col-span-2 flex gap-2 mt-2">
        <button onClick={onSave} disabled={saving || !form.make || !form.model}
          className="flex-1 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="save-vehicle-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {saveLabel}
        </button>
        <button onClick={onCancel}
          className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm hover:bg-slate-200">
          Abbrechen
        </button>
      </div>
    </div>
  );
}

export function VehicleManager({ vehicles = [], onRefresh, t }) {
  const [activeVehicleId, setActiveVehicleId] = useState(vehicles[0]?.id || null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(emptyVehicle);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [message, setMessage] = useState(null);

  const activeVehicle = vehicles.find(v => v.id === activeVehicleId) || vehicles[0];

  const handleAdd = async () => {
    setSaving(true);
    try {
      const res = await api.post('/driver/vehicle/add', form);
      setShowAddForm(false);
      setForm(emptyVehicle);
      setMessage({ text: 'Fahrzeug hinzugefügt', type: 'success' });
      if (onRefresh) onRefresh();
    } catch (e) {
      setMessage({ text: e.response?.data?.detail || 'Fehler', type: 'error' });
    } finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!activeVehicle) return;
    setSaving(true);
    try {
      await api.put(`/driver/vehicle/${activeVehicle.id}`, form);
      setEditMode(false);
      setMessage({ text: 'Fahrzeug aktualisiert', type: 'success' });
      if (onRefresh) onRefresh();
    } catch (e) {
      setMessage({ text: e.response?.data?.detail || 'Fehler', type: 'error' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Fahrzeug wirklich entfernen?')) return;
    try {
      await api.delete(`/driver/vehicle/${vehicleId}`);
      setMessage({ text: 'Fahrzeug entfernt', type: 'success' });
      setActiveVehicleId(vehicles.find(v => v.id !== vehicleId)?.id || null);
      if (onRefresh) onRefresh();
    } catch (e) {
      setMessage({ text: e.response?.data?.detail || 'Fehler', type: 'error' });
    }
  };

  const handleDocUpload = async (vehicleId, docType, file) => {
    const key = `${vehicleId}_${docType}`;
    setUploading(u => ({ ...u, [key]: true }));
    try {
      const dataUrl = await compressIfImage(file);
      await api.post(`/driver/vehicle/${vehicleId}/upload-document/${docType}`, { document: dataUrl });
      setMessage({ text: `${DOC_LABELS[docType]} hochgeladen`, type: 'success' });
      if (onRefresh) onRefresh();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Upload fehlgeschlagen';
      setMessage({ text: `Upload fehlgeschlagen: ${msg}`, type: 'error' });
    } finally {
      setUploading(u => ({ ...u, [key]: false }));
    }
  };

  const startEdit = () => {
    if (!activeVehicle) return;
    setForm({
      make: activeVehicle.make, model: activeVehicle.model, year: activeVehicle.year,
      color: activeVehicle.color, plate_number: activeVehicle.plate_number,
      vehicle_type: activeVehicle.vehicle_type, passengers: activeVehicle.passengers, luggage: activeVehicle.luggage,
    });
    setEditMode(true);
  };

  const handleCancel = () => { setShowAddForm(false); setEditMode(false); };

  const StatusIcon = STATUS_BADGE[activeVehicle?.status]?.icon || Clock;

  return (
    <div className="space-y-4" data-testid="vehicle-manager">
      {message && (
        <div className={`p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : message.type === 'info' ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`}>
          {message.text}
        </div>
      )}

      {/* Vehicle Switcher */}
      <div className="flex items-center gap-2 flex-wrap">
        <Car size={16} className="text-slate-400" />
        <select value={activeVehicleId || ''} onChange={e => { setActiveVehicleId(e.target.value); setEditMode(false); }}
          className="flex-1 max-w-xs px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium bg-white"
          data-testid="vehicle-switcher">
          {vehicles.map(v => (
            <option key={v.id} value={v.id}>{v.make} {v.model} ({v.plate_number})</option>
          ))}
        </select>
        {vehicles.length < 4 && (
          <button onClick={() => { setShowAddForm(true); setForm(emptyVehicle); setEditMode(false); }}
            className="flex items-center gap-1 px-3 py-2 bg-orange-50 text-orange-600 rounded-lg text-xs font-medium hover:bg-orange-100"
            data-testid="add-vehicle-btn">
            <Plus size={14} /> Fahrzeug hinzufügen
          </button>
        )}
      </div>

      {/* Add Vehicle Form */}
      {showAddForm && (
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
          <h3 className="font-medium text-sm text-slate-800 mb-3">Neues Fahrzeug hinzufügen</h3>
          <VehicleForm form={form} setForm={setForm} onSave={handleAdd} saveLabel="Hinzufügen" saving={saving} onCancel={handleCancel} />
        </div>
      )}

      {/* Active Vehicle Details */}
      {activeVehicle && !showAddForm && (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          {/* Vehicle Header */}
          <div className="px-4 py-3 bg-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm text-slate-800">{activeVehicle.make} {activeVehicle.model}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${STATUS_BADGE[activeVehicle.status]?.cls}`}>
                <StatusIcon size={12} /> {STATUS_BADGE[activeVehicle.status]?.label}
              </span>
            </div>
            <div className="flex gap-1">
              {!editMode && (
                <button onClick={startEdit} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded" data-testid="edit-vehicle-btn">
                  <Edit2 size={14} />
                </button>
              )}
              {vehicles.length > 1 && (
                <button onClick={() => handleDelete(activeVehicle.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded" data-testid="delete-vehicle-btn">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Edit or View Mode */}
          <div className="p-4">
            {editMode ? (
              <VehicleForm form={form} setForm={setForm} onSave={handleUpdate} saveLabel="Speichern" saving={saving} onCancel={handleCancel} />
            ) : (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-400 text-xs">Hersteller</span><p className="text-slate-800">{activeVehicle.make}</p></div>
                <div><span className="text-slate-400 text-xs">Modell</span><p className="text-slate-800">{activeVehicle.model}</p></div>
                <div><span className="text-slate-400 text-xs">Baujahr</span><p className="text-slate-800">{activeVehicle.year}</p></div>
                <div><span className="text-slate-400 text-xs">Farbe</span><p className="text-slate-800">{activeVehicle.color}</p></div>
                <div><span className="text-slate-400 text-xs">Kennzeichen</span><p className="text-slate-800">{activeVehicle.plate_number}</p></div>
                <div><span className="text-slate-400 text-xs">Typ</span><p className="text-slate-800">{activeVehicle.vehicle_type}</p></div>
                <div><span className="text-slate-400 text-xs">Passagiere</span><p className="text-slate-800">{activeVehicle.passengers}</p></div>
                <div><span className="text-slate-400 text-xs">Gepäck</span><p className="text-slate-800">{activeVehicle.luggage}</p></div>
              </div>
            )}
          </div>

          {/* Vehicle Documents */}
          <div className="border-t border-slate-200 px-4 py-3">
            <h4 className="text-xs font-medium text-slate-500 mb-3 flex items-center gap-1"><FileText size={12} /> Fahrzeug-Dokumente</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {Object.entries(DOC_LABELS).map(([key, label]) => {
                const docVal = activeVehicle.documents?.[key];
                const hasDoc = docVal && docVal !== 'pending' && docVal !== '';
                const isUploading = uploading[`${activeVehicle.id}_${key}`];
                return (
                  <div key={key} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`vehicle-doc-${key}`}>
                    <div className="px-3 py-2 bg-slate-50 flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-600">{label}</span>
                      <span className={`text-xs ${hasDoc ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {hasDoc ? '✓' : 'Fehlt'}
                      </span>
                    </div>
                    {hasDoc ? (
                      <div className="h-24 relative group">
                        <img src={docVal} alt={label} className="w-full h-full object-cover" />
                        <div className="absolute bottom-0 left-0 right-0 flex">
                          <button onClick={() => { const w = window.open('', '_blank'); w.document.write(`<html><body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;min-height:100vh"><img src="${docVal}" style="max-width:100%;max-height:100vh;object-fit:contain"/></body></html>`); w.document.title = label; }} className="flex-1 bg-black/50 text-white text-[10px] text-center py-1 hover:bg-black/70 transition-colors">Anzeigen</button>
                          <label className="flex-1 bg-black/50 text-white text-[10px] text-center py-1 hover:bg-black/70 transition-colors cursor-pointer">
                            Ersetzen
                            <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && handleDocUpload(activeVehicle.id, key, e.target.files[0])} />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <label className="h-24 flex flex-col items-center justify-center bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors">
                        {isUploading ? <Loader2 size={18} className="animate-spin text-orange-500" /> : <Upload size={18} className="text-slate-300 mb-1" />}
                        <span className="text-xs text-slate-400">Hochladen</span>
                        <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && handleDocUpload(activeVehicle.id, key, e.target.files[0])} />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
            <button onClick={() => { setMessage({ text: 'Fortschritt gespeichert. Sie können jederzeit weiter hochladen.', type: 'info' }); }}
              className="mt-3 w-full py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200 transition-colors"
              data-testid="upload-later-btn">
              Später weiter hochladen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
